import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert } from "@mui/material";
import { toast } from "react-toastify";
import { useUsers } from "../../hooks/useRedux";
import UserForm from "./UserForm";
import { User } from "../../store/slices/types";

const EditUser: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { users, updateUser, loading } = useUsers();

  const user = users.find(
    (u) => (u._id || u.id || u.uid) === id
  ) as User | undefined;

  const handleSave = async (userData: Partial<User>) => {
    if (!id) {
      toast.error("Invalid user ID");
      return;
    }

    try {
      await updateUser(id, userData);
      toast.success("user is updated");
      navigate("/users");
    } catch (error) {
      console.error("Failed to update user:", error);
      toast.error(
        `❌ Failed to update user: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  const handleCancel = () => {
    navigate("/users");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          User not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          mb: 4,
        }}
      >
        <Typography variant='h4' fontWeight='700' gutterBottom>
          Edit User
        </Typography>
        <Typography variant='body1' sx={{ opacity: 0.9 }}>
          Update user information and preferences
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <UserForm
          user={user}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </Paper>
    </Box>
  );
};

export default EditUser;

